/**
 * Persons Pivots — John Person's classic floor pivots, plotted as a step-line
 * overlay on the price pane, for one of five interval cycles:
 *
 *   D  Daily            calendar UTC day
 *   W  Weekly           ISO Monday-start week
 *   M  Monthly          calendar month
 *   O  Option Expiration the monthly opex cycle that ENDS on the 3rd Friday
 *   Q  Quarterly        calendar quarter (Jan-Mar, Apr-Jun, …)
 *
 * For every interval the levels use the PRIOR COMPLETED bucket only (the classic
 * pivot convention — stable levels that don't repaint intra-period), with
 *
 *   P  = (priorHigh + priorLow + priorClose) / 3
 *   R1 = 2P − priorLow      S1 = 2P − priorHigh
 *   R2 = P + (priorHigh − priorLow)   S2 = P − (priorHigh − priorLow)
 *
 * matching the weekly/monthly pivots the Person Screener already computes in
 * `person.ts`. Each daily bar carries the levels of the bucket immediately
 * before its own (in-progress) bucket, so the plot is a staircase that steps to
 * a fresh set of levels at every period boundary.
 */
import { familyGroup } from "../indicatorGroups";
import { TF_DWM, TF_PARAM } from "./timeframe";
import type { OHLCV, IndicatorParams, SharedIndicatorDef } from "../types";

export type PivotInterval = "D" | "W" | "M" | "O" | "Q";

/** Which period's prices feed the pivot:
 *  - "prior"   the LAST CLOSED period (classic, stable, no-repaint). While the
 *              current week/day/month is still open, its pivot is last period's.
 *  - "current" the CURRENT period the bar sits in (the in-progress week/day/…),
 *              so the live period's pivot develops from its own prices. */
export type PivotBasis = "prior" | "current";

export type PivotLevels = {
  p: (number | null)[];
  /** PIPA / PPMA — the trailing-3 SMA of the period Pivot (P[n]+P[n-1]+P[n-2])/3. */
  pipa: (number | null)[];
  r1: (number | null)[];
  r2: (number | null)[];
  s1: (number | null)[];
  s2: (number | null)[];
};

/** UTC date parts for a ms timestamp. */
function ymd(t: number): { y: number; m: number; d: number; dow: number } {
  const dt = new Date(t);
  return {
    y: dt.getUTCFullYear(),
    m: dt.getUTCMonth(), // 0-11
    d: dt.getUTCDate(),
    dow: dt.getUTCDay(), // 0=Sun … 6=Sat
  };
}

/** The day-of-month of the 3rd Friday of the given UTC year/month (0-based m). */
function thirdFriday(y: number, m: number): number {
  const firstDow = new Date(Date.UTC(y, m, 1)).getUTCDay(); // 0=Sun
  // First Friday (dow 5) date, then add two weeks.
  const firstFri = 1 + ((5 - firstDow + 7) % 7);
  return firstFri + 14;
}

/** Bucket key for a timestamp under the chosen interval. Keys are ordinal-stable
 *  strings (lexically increasing in time within an interval). */
function pivotBucketKey(t: number, interval: PivotInterval): string {
  const { y, m, d, dow } = ymd(t);
  const mm = String(m + 1).padStart(2, "0");
  switch (interval) {
    case "D":
      return `${y}-${mm}-${String(d).padStart(2, "0")}`;
    case "M":
      return `${y}-${mm}`;
    case "Q":
      return `${y}-Q${Math.floor(m / 3) + 1}`;
    case "O": {
      // The opex cycle a date belongs to is the one ENDING on the next 3rd
      // Friday ≥ the date. On/before this month's 3rd Friday → this month's
      // cycle; after it → next month's.
      const tf = thirdFriday(y, m);
      // A Saturday/Sunday after the 3rd Friday still rolls into next cycle.
      const afterExpiry = d > tf || (d === tf && dow !== 5);
      if (!afterExpiry) return `${y}-${mm}`;
      const ny = m === 11 ? y + 1 : y;
      const nm = m === 11 ? 0 : m + 1;
      return `${ny}-${String(nm + 1).padStart(2, "0")}`;
    }
    case "W":
    default: {
      // ISO week (Monday-start), Thursday-anchored — same algorithm as
      // weekly.ts `bucketKey`, inlined to avoid widening its HtFreq type.
      const dt = new Date(Date.UTC(y, m, d));
      const day = dt.getUTCDay() || 7;
      dt.setUTCDate(dt.getUTCDate() + 4 - day);
      const yearStart = new Date(Date.UTC(dt.getUTCFullYear(), 0, 1));
      const week = Math.ceil(((dt.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
      return `${dt.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
    }
  }
}

type Bucket = { key: string; h: number; l: number; c: number };

/**
 * Floor-pivot levels for every daily bar of the chosen interval. `basis` picks
 * the source period: "prior" (default) uses the LAST CLOSED period — the classic
 * stable convention, so the in-progress week/day/month keeps last period's pivot
 * — while "current" uses the bar's OWN (in-progress) period, so the live period's
 * pivot develops from its own prices. Levels are null until the source exists.
 */
export function personsPivots(
  times: ReadonlyArray<number>,
  highs: ReadonlyArray<number>,
  lows: ReadonlyArray<number>,
  closes: ReadonlyArray<number>,
  interval: PivotInterval,
  basis: PivotBasis = "prior",
): PivotLevels {
  const n = closes.length;
  const empty = (): (number | null)[] => new Array<number | null>(n).fill(null);
  const out: PivotLevels = { p: empty(), pipa: empty(), r1: empty(), r2: empty(), s1: empty(), s2: empty() };
  if (n === 0) return out;

  // Ordered list of completed buckets + each daily bar's bucket ordinal.
  const buckets: Bucket[] = [];
  const ordinalOf = new Array<number>(n);
  let cur: Bucket | null = null;
  for (let i = 0; i < n; i++) {
    const k = pivotBucketKey(times[i]!, interval);
    if (!cur || cur.key !== k) {
      cur = { key: k, h: highs[i]!, l: lows[i]!, c: closes[i]! };
      buckets.push(cur);
    } else {
      if (highs[i]! > cur.h) cur.h = highs[i]!;
      if (lows[i]! < cur.l) cur.l = lows[i]!;
      cur.c = closes[i]!;
    }
    ordinalOf[i] = buckets.length - 1; // index of the bar's (in-progress) bucket
  }

  // Source period for each bar's pivot: the prior closed bucket (offset 1) or the
  // bar's own in-progress bucket (offset 0).
  const srcOffset = basis === "current" ? 0 : 1;

  // Pivot per period ordinal (from its source bucket). Used both for the per-bar
  // pivot and for PIPA = the trailing-3 SMA of that pivot series.
  const pivotOfPeriod = new Array<number | null>(buckets.length).fill(null);
  for (let ord = 0; ord < buckets.length; ord++) {
    const srcIdx = ord - srcOffset;
    if (srcIdx < 0) continue;
    const src = buckets[srcIdx]!;
    pivotOfPeriod[ord] = (src.h + src.l + src.c) / 3;
  }

  for (let i = 0; i < n; i++) {
    const ord = ordinalOf[i]!;
    const srcIdx = ord - srcOffset;
    if (srcIdx < 0) continue; // no source period yet
    const src = buckets[srcIdx]!;
    const p = (src.h + src.l + src.c) / 3;
    const range = src.h - src.l;
    out.p[i] = p;
    out.r1[i] = 2 * p - src.l;
    out.s1[i] = 2 * p - src.h;
    out.r2[i] = p + range;
    out.s2[i] = p - range;
    // PIPA / PPMA = SMA of the current + two prior period pivots.
    const a = pivotOfPeriod[ord], b = pivotOfPeriod[ord - 1], c = pivotOfPeriod[ord - 2];
    if (a != null && b != null && c != null) out.pipa[i] = (a + b + c) / 3;
  }
  return out;
}

export const PIVOT_INTERVAL_LABEL: Record<PivotInterval, string> = {
  D: "Daily",
  W: "Weekly",
  M: "Monthly",
  O: "Option Expiration",
  Q: "Quarterly",
};

// ── Scanner indicators — floor-pivot levels per timeframe (Person group) ──────
// The screener's Pivot / Pivot MA / Support / Resistance + their %-distances,
// surfaced as configurable Daily/Weekly/Monthly indicators (recompute via
// personsPivots — same prior-completed-bucket math as the baked jp_w/m_* columns).
const PIVOT_GROUP = familyGroup("pivots"); // → "Pivot Studies"

const tfInterval = (tf: number | undefined): PivotInterval =>
  tf === 2 ? "M" : tf === 1 ? "W" : "D";

const pivotLevelsOf = (d: OHLCV, p: IndicatorParams): PivotLevels => {
  const { times, highs, lows, closes } = d;
  if (!times || !highs || !lows || times.length !== closes.length) {
    const e = () => new Array<number | null>(closes.length).fill(null);
    return { p: e(), pipa: e(), r1: e(), r2: e(), s1: e(), s2: e() };
  }
  return personsPivots(times, highs, lows, closes, tfInterval(p[TF_PARAM]), "prior");
};

// panelKey = the Daily served field (the default tf); Weekly/Monthly resolve via
// TF_PRECOMPUTED (dynamicIndicatorColumns) reading the *_w / *_m payload fields.
/** A pivot LEVEL indicator (price; 2-decimal) — Pivot / Pivot MA / S1 / R1. */
const levelDef = (
  id: string, label: string, panelKey: string, pick: (lv: PivotLevels) => (number | null)[],
): SharedIndicatorDef => ({
  id, label, group: PIVOT_GROUP, format: "ratio", needsComparative: false, panelKey,
  params: [TF_DWM],
  computeSeries: (d, p) => pick(pivotLevelsOf(d, p)),
});

/** A %-distance from the close to a level. Support: (close − S1) / close · 100;
 *  Resistance: (R1 − close) / close · 100 (positive when the level is on its
 *  expected side). */
const distDef = (
  id: string, label: string, panelKey: string, pick: (lv: PivotLevels) => (number | null)[], support: boolean,
): SharedIndicatorDef => ({
  id, label, group: PIVOT_GROUP, format: "signed", panelKey,
  params: [TF_DWM],
  computeSeries: (d, p) => {
    const line = pick(pivotLevelsOf(d, p));
    return d.closes.map((c, i) => {
      const x = line[i];
      if (x == null || c == null || c === 0) return null;
      return Math.round((support ? ((c - x) / c) : ((x - c) / c)) * 100 * 100) / 100;
    });
  },
});

export const PIVOT_VALUE = levelDef("mpp-jp-pivot", "Afiat - Floor Pivot", "pivot_d", (lv) => lv.p);
export const PIVOT_MA = levelDef("mpp-jp-pivot-ma", "Afiat - Pivot Average", "pivotma_d", (lv) => lv.pipa);
export const SUPPORT_VALUE = levelDef("mpp-jp-support", "Afiat - Pivot Support (S1)", "support_d", (lv) => lv.s1);
export const RESISTANCE_VALUE = levelDef("mpp-jp-resistance", "Afiat - Pivot Resistance (R1)", "resist_d", (lv) => lv.r1);
export const DIST_SUPPORT = distDef("mpp-jp-dist-support", "Afiat - Distance to S1", "distsup_d", (lv) => lv.s1, true);
export const DIST_RESISTANCE = distDef("mpp-jp-dist-resistance", "Afiat - Distance to R1", "distres_d", (lv) => lv.r1, false);

export const PIVOT_INDICATORS: ReadonlyArray<SharedIndicatorDef> = [
  PIVOT_VALUE, PIVOT_MA, SUPPORT_VALUE, DIST_SUPPORT, RESISTANCE_VALUE, DIST_RESISTANCE,
];
